import React, { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, formatApiError } from "@/shared/lib/api";
import { toast } from "sonner";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const CYCLES = [
  { id: "weekly", name: "Weekly" },
  { id: "biweekly", name: "Every 2 weeks" },
  { id: "monthly", name: "Every 4 weeks" },
];

/**
 * Edit an existing route (days / hours / billing cycle / day overrides).
 * Backend enforces the 48-hour rule; this dialog surfaces any 400 from the server.
 * Addresses, buffer, and tier are not editable here (require re-pricing).
 */
export default function RouteEditDialog({ open, onOpenChange, route, onSaved }) {
  const [days, setDays] = useState(route?.days || []);
  const [arrival, setArrival] = useState(route?.arrival_time || "08:30");
  const [ret, setRet] = useState(route?.return_time || "17:00");
  const [cycle, setCycle] = useState(route?.billing_cycle || "weekly");
  const [overrides, setOverrides] = useState(route?.day_overrides || {});
  const [busy, setBusy] = useState(false);

  const locked = useMemo(() => {
    // Pessimistic client-side hint — backend is the source of truth
    return false;
  }, []);

  if (!route) return null;

  const toggleDay = (d) => setDays((arr) => (arr.includes(d) ? arr.filter((x) => x !== d) : [...arr, d]));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/routes/${route.id}`, {
        days, arrival_time: arrival, return_time: ret, billing_cycle: cycle, day_overrides: overrides,
      });
      toast.success("Route updated");
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-[#F9F7F4] border hairline rounded-none p-7" data-testid="route-edit-dialog">
        <DialogHeader className="text-left space-y-2">
          <div className="label-xs text-[#ed6500]">Edit route</div>
          <DialogTitle className="font-serif font-light text-2xl leading-tight">{route.pickup_address}</DialogTitle>
          <DialogDescription className="text-xs text-[#515E7B]">
            Edits are allowed up to 48 hours before your next pickup. Addresses cannot be changed here — create a new route if your origin or destination changes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          <div>
            <div className="label-xs mb-2">Days</div>
            <div className="flex flex-wrap gap-2">
              {DAYS.map((d) => (
                <button key={d} onClick={() => toggleDay(d)} className={`day-chip ${days.includes(d) ? "selected" : ""}`} data-testid={`edit-day-${d.toLowerCase()}`}>{d}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="label-xs mb-2">Arrive at work by</div>
              <input type="time" className="input-line text-sm" value={arrival} onChange={(e) => setArrival(e.target.value)} data-testid="edit-arrival"/>
            </div>
            <div>
              <div className="label-xs mb-2">Leave work by</div>
              <input type="time" className="input-line text-sm" value={ret} onChange={(e) => setRet(e.target.value)} data-testid="edit-return"/>
            </div>
          </div>
          <div>
            <div className="label-xs mb-2">Billing</div>
            <div className="flex flex-wrap gap-2">
              {CYCLES.map((c) => (
                <button key={c.id} onClick={() => setCycle(c.id)} className={`day-chip ${cycle === c.id ? "selected" : ""}`} data-testid={`edit-cycle-${c.id}`}>{c.name}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t hairline">
          <button onClick={() => onOpenChange(false)} className="text-[11px] tracking-widest uppercase px-4 py-2.5 border hairline hover:bg-white" data-testid="edit-cancel">Cancel</button>
          <button onClick={save} disabled={busy || locked} className="btn-primary text-[11px] tracking-widest uppercase disabled:opacity-60" data-testid="edit-save">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
